/** Debug overlay (?debug=1): live DOM readouts + episode recovery download. */

import type { Simulation } from "../sim/simulation";
import type { EpisodeData } from "../telemetry/schema";
import { readArchive, downloadEpisode } from "../telemetry/download";

export class DebugOverlay {
  private readonly el: HTMLElement;
  private readonly text: HTMLElement;

  /**
   * @param getCurrent returns a snapshot of the current UNFINISHED attempt
   * (outcome "incomplete"), or null when the attempt has already finalized —
   * this is the documented mid-attempt recovery path (DATA_SCHEMA.md §7).
   */
  constructor(root: HTMLElement, getCurrent: () => EpisodeData | null) {
    const el = document.createElement("div");
    el.id = "debug-overlay";
    el.innerHTML = `<pre id="debug-text"></pre><button id="btn-debug-download" type="button">Download last episodes</button>`;
    root.appendChild(el);
    this.el = el;
    this.text = el.querySelector("#debug-text") as HTMLElement;
    (el.querySelector("#btn-debug-download") as HTMLButtonElement).addEventListener(
      "click",
      () => {
        for (const e of readArchive()) downloadEpisode(e);
        const current = getCurrent();
        if (current) downloadEpisode(current);
      },
    );
  }

  update(sim: Simulation): void {
    const r = sim.robot;
    const ped = sim.pedModel?.state ?? null;
    const s = sim.lastSafety;
    const marks = sim.events
      .filter((e) =>
        ["cue_onset", "first_commitment", "research_window_end", "passing_moment"].includes(e.type),
      )
      .map((e) => `${e.type}@${e.t.toFixed(2)}s`)
      .join("  ");
    const c = sim.corridor;
    const corridorLine = c
      ? `corridor ${c.state} len=${c.length.toFixed(1)} cap=${c.speedCap.toFixed(2)} ` +
        `w=${c.width.toFixed(2)} ${c.priorityId ? `yield:${c.priorityId} ` : ""}` +
        `${c.conflictS !== null ? `conflict:${c.conflictS.toFixed(1)}m ` : ""}(${c.reason})`
      : null;
    this.text.textContent = [
      `t=${sim.t.toFixed(2)}s  phase=${sim.phase}  safety=${sim.safetyState}`,
      `robot x=${r.x.toFixed(2)} z=${r.z.toFixed(2)} h=${((r.heading * 180) / Math.PI).toFixed(0)}°`,
      `      v=${r.speed.toFixed(2)}m/s  yaw=${r.yawRate.toFixed(2)}rad/s`,
      ped && ped.active
        ? `ped   x=${ped.x.toFixed(2)} z=${ped.z.toFixed(2)} v=(${ped.vx.toFixed(2)},${ped.vz.toFixed(2)})`
        : `ped   —`,
      s
        ? `ttc=${Number.isFinite(s.ttc) ? s.ttc.toFixed(2) + "s" : "∞"}  free=${s.freeDist.toFixed(2)}m  cap=${s.speedCap.toFixed(2)}m/s`
        : ``,
      marks ? `events: ${marks}` : `events: —`,
    ].join("\n") + (corridorLine ? `\n${corridorLine}` : "");
  }

  setVisible(v: boolean): void {
    this.el.style.display = v ? "" : "none";
  }
}
